import { priceDateKey } from '@/utils/circuits/prices/priceDateKey';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import LinkIcon from '@/assets/icons/link-01.svg?react';
import CircuitDetailsImagesCard from '@/components/distributorCircuits/CircuitDetailsImagesCard';
import CircuitDetailsItineraryCard from '@/components/distributorCircuits/CircuitDetailsItineraryCard';
import { CircuitDetailsParticipantsCard } from '@/components/distributorCircuits/CircuitDetailsParticipantsCard';
import CircuitDetailsPricesCalendarCard from '@/components/distributorCircuits/CircuitDetailsPricesCalendarCard';
import CircuitDetailsSummaryCard from '@/components/distributorCircuits/CircuitDetailsSummaryCard';
import EstimateDetailsAndOptions from '@/components/estimates/EstimateDetailsAndOptions';
import ReservationRequestAcceptedModal from '@/components/estimates/ReservationRequestAcceptedModal';
import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledStepper from '@/components/styled/StyledStepper';
import HorizontalLine from '@/components/ui/HorizontalLine';
import Loader from '@/components/ui/Loader';
import { Switch } from '@/components/ui/switch';
import { PATHS } from '@/constants/Paths';
import { computeInternationalFlightsTotal } from '@/utils/estimates/internationalFlightsPricing';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import {
  useCreateClientSharedLink,
  useGetEstimateById,
  useGetEstimateSharedLinkToken,
  useRevokeClientSharedLink,
  useRevokeSharedLink,
  useValidateEstimateParagraph,
} from '@/hooks/useEstimate';
import { useSubmitEstimate } from '@/hooks/useSubmitEstimate';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import {
  EstimateFormData,
  EstimateSchema,
} from '@/types/estimates/EstimateFormData';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { SupportedLocale } from '@/types/supported-locale.type';
import { computeBenefitPrices } from '@/utils/circuits/prices/computeBenefitPrices';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { getDatesToPrice } from '@/utils/circuits/prices/pricingHorizon';
import { formatEstimateRef } from '@/utils/displayCodes';
import { loadEstimateDefaultValues } from '@/utils/estimates/loadEstimateDefaultValues';
import { resolveEstimateDisplayTitle } from '@/utils/estimates/resolveEstimateDisplayTitle';
import {
  shouldHideAgencyFlexibilityContent,
  shouldMuteAgencyFlexibilityContent,
} from '@/utils/estimates/shouldHideAgencyFlexibilityContent';
import { resolveIsAgencyLinkView } from '@/utils/estimates/resolveIsAgencyLinkView';
import { applyAgencyAdditionalMarginToTotal } from '@/utils/estimates/applyAgencyAdditionalMargin';
import { formatFirstFormError } from '@/utils/form/formatFirstFormError';
import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, differenceInYears, startOfToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { getTranslatedCountry } from '../../../../../../shared/CountriesData';
import Modal from '@/components/ui/Modal';

type EstimatePageSource = 'projectEstimates' | 'allEstimates';

type SharedLinkType = EstimateResponse['SharedLink'][number]['type'];

const isSharedLinkActive = (
  links: EstimateResponse['SharedLink'],
  type: SharedLinkType,
) => {
  // Defensive: the API may return the relation as an array, a single object or
  // null depending on the backend version — never assume `.find` exists.
  const list = Array.isArray(links) ? links : links ? [links] : [];
  const link = list.find((l) => l.type === type);
  return link != null && !link.revoked;
};

const ViewEstimatePage = ({
  source = 'projectEstimates',
}: {
  source?: EstimatePageSource;
}) => {
  const { projectId = '', estimateId = '', clientId = '' } = useParams();

  const {
    data: estimateResponse,
    isLoading,
    isSuccess,
    isFetchedAfterMount,
  } = useGetEstimateById({
    estimateId,
    projectId,
  });

  return isLoading || !isFetchedAfterMount ? (
    <Loader />
  ) : (
    isSuccess && (
      <EstimateDetails
        estimateResponse={estimateResponse}
        projectId={projectId}
        clientId={clientId}
        source={source}
        key={estimateResponse.id}
      />
    )
  );
};

export default ViewEstimatePage;

interface EstimateDetailsProps {
  estimateResponse: EstimateResponse;
  projectId: string;
  clientId?: string;
  source?: EstimatePageSource;
  accessToken?: string;
  shouldHideInternationalFlights?: boolean;
}

export const EstimateDetails = ({
  estimateResponse,
  projectId,
  clientId,
  shouldHideInternationalFlights = false,
  source,
  accessToken,
}: EstimateDetailsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isTourOperator } = useDistributorLabels();

  const isToView = estimateResponse.viewContext === 'TO_AUTHENTICATED';
  const isAgencyView = resolveIsAgencyLinkView(estimateResponse, accessToken);
  const isClientLink = estimateResponse.viewContext === 'CLIENT_LINK';
  /** Client link is read-only for dates; agency link may pick a departure date. */
  const canSelectDepartureDate = !accessToken || isAgencyView;
  /**
   * Souplesse TO : le réceptif peut masquer le calendrier (« Afficher les
   * dates » = non) dans le devis de l'agence.
   */
  const hideAgencyDepartureCalendar =
    isAgencyView &&
    estimateResponse.allowAgencyModification === true &&
    estimateResponse.showDates === false;
  /**
   * Souplesse TO : sur le lien client, seule l'agence confirme la réservation.
   * Le bouton « Confirmer la demande de réservation » est masqué pour le client.
   */
  const hideClientReservationConfirm =
    isClientLink && estimateResponse.allowAgencyModification === true;
  /** Souplesse TO : côté agence, le client final a déjà adressé sa demande. */
  const clientHasSentRequest =
    isAgencyView && Boolean(estimateResponse.clientRequestSentAt);
  /** Souplesse TO : côté client, la demande a déjà été adressée à l'agence. */
  const clientRequestAlreadySent =
    isClientLink && Boolean(estimateResponse.clientRequestSentAt);
  /** Paragraphes éditoriaux : modifiables tant que le devis n'est pas réservé/refusé. */
  const canEditCircuitParagraphs =
    !accessToken &&
    estimateResponse.status !== EstimateStatus.RESERVED &&
    estimateResponse.status !== EstimateStatus.REFUSED;

  const estimatesListPath =
    source === 'projectEstimates' && clientId
      ? PATHS.distributors.clients.estimates.list
          .replace(':clientId', clientId)
          .replace(':projectId', projectId)
      : PATHS.distributors.estimates.list;
  const estimateLanguage = (
    estimateResponse.language ?? 'FR'
  ).toLowerCase() as SupportedLocale;

  const ESTIMATE_STEPS = [
    t('estimates.steps.circuitDescription'),
    t('estimates.steps.detailsAndOptions'),
    t('estimates.steps.reservationRequest'),
  ];

  const [nbAdults, setNbAdults] = useState(estimateResponse.adults.length);
  const [nbChildren, setNbChildren] = useState(
    estimateResponse.children.length,
  );
  const [childrenBirthDates, setChildrenBirthDates] = useState<
    Record<number, Date>
  >(estimateResponse.children.map(({ birthDate }) => new Date(birthDate)));

  const parsedEstimate = useMemo(
    () => loadEstimateDefaultValues({ estimateResponse, t }),
    [estimateResponse, t],
  );

  const methods = useForm<EstimateFormData>({
    mode: 'onChange',
    resolver: zodResolver(EstimateSchema),
    defaultValues: parsedEstimate,
  });

  const childrenAges = useMemo(
    () =>
      Object.values(childrenBirthDates).map((birthdate) =>
        differenceInYears(startOfToday(), birthdate),
      ),
    [childrenBirthDates],
  );

  const { control, handleSubmit, reset } = methods;
  const { toastAsync } = useToast();

  const { mutateAsync: validateParagraphMutation } =
    useValidateEstimateParagraph(projectId, estimateResponse.id);

  const handleValidateParagraph = useCallback(
    async (field: string, value: string) => {
      await toastAsync(validateParagraphMutation({ field, value }), {
        pending: t('common.loading'),
        success: t('common.success'),
        error: t('common.error'),
      });
    },
    [t, toastAsync, validateParagraphMutation],
  );

  const [currStep, setCurrStep] = useState(0);
  const [
    isReservationRequestAcceptedModalOpen,
    setIsReservationRequestAcceptedModalOpen,
  ] = useState(false);
  const [isReservationBlockedModalOpen, setIsReservationBlockedModalOpen] =
    useState(false);

  const estimateMarginRate = useMemo(
    () =>
      estimateResponse.marginRate ??
      estimateResponse.project.client.marginRate ??
      estimateResponse.reservationAgent.agency.marginRate ??
      0,
    [
      estimateResponse.marginRate,
      estimateResponse.project.client.marginRate,
      estimateResponse.reservationAgent.agency.marginRate,
    ],
  );

  const watchedAgencyMarginRate = useWatch({
    control,
    name: 'agencyMarginRate',
    disabled: !isAgencyView,
  });

  const watchedAdditionalMarginType = useWatch({
    control,
    name: 'agencyAdditionalMarginType',
    disabled: !isAgencyView,
  });
  const watchedAdditionalMarginValue = useWatch({
    control,
    name: 'agencyAdditionalMarginValue',
    disabled: !isAgencyView,
  });

  /**
   * Extra agency margin on the FINAL TOTAL (agency edits, client pays, TO sees
   * for control). Not applied per prestation.
   */
  const applyAgencyAdditionalMargin = useCallback(
    (margedTotal: number) => {
      if (!estimateResponse.allowAgencyAdditionalMargin) return margedTotal;
      if (!isAgencyView && !isClientLink && !isToView) return margedTotal;

      let type: 'FIXED' | 'PERCENT' =
        estimateResponse.agencyAdditionalMarginType ?? 'FIXED';
      let value: number | undefined;

      if (isAgencyView) {
        const liveValue =
          watchedAdditionalMarginValue != null &&
          !Number.isNaN(Number(watchedAdditionalMarginValue))
            ? Number(watchedAdditionalMarginValue)
            : undefined;
        value =
          liveValue ??
          estimateResponse.agencyAdditionalMarginValue ??
          undefined;
        type =
          (watchedAdditionalMarginType as 'FIXED' | 'PERCENT' | undefined) ??
          estimateResponse.agencyAdditionalMarginType ??
          'FIXED';
      } else {
        value = estimateResponse.agencyAdditionalMarginValue ?? undefined;
        type = estimateResponse.agencyAdditionalMarginType ?? 'FIXED';
      }

      return applyAgencyAdditionalMarginToTotal(margedTotal, { type, value });
    },
    [
      estimateResponse.agencyAdditionalMarginType,
      estimateResponse.agencyAdditionalMarginValue,
      estimateResponse.allowAgencyAdditionalMargin,
      isAgencyView,
      isClientLink,
      isToView,
      watchedAdditionalMarginType,
      watchedAdditionalMarginValue,
    ],
  );

  /** Agency overlay margin baked into circuit prices at load (excludes TO margin). */
  const marginAppliedAtLoad = useMemo(
    () =>
      isAgencyView
        ? (estimateResponse.agencyMarginRate ?? 0)
        : estimateMarginRate,
    [estimateMarginRate, estimateResponse.agencyMarginRate, isAgencyView],
  );

  const effectiveEstimateMarginRate = useMemo(() => {
    if (!isAgencyView) return estimateMarginRate;
    if (
      watchedAgencyMarginRate != null &&
      !Number.isNaN(Number(watchedAgencyMarginRate))
    ) {
      return Number(watchedAgencyMarginRate);
    }
    return marginAppliedAtLoad;
  }, [
    estimateMarginRate,
    isAgencyView,
    marginAppliedAtLoad,
    watchedAgencyMarginRate,
  ]);

  /** Re-scale API-margined prices when the agency edits its margin live. */
  const agencyMarginPriceFactor = useMemo(() => {
    if (!isAgencyView) return 1;
    const oldM = marginAppliedAtLoad;
    const newM = effectiveEstimateMarginRate;
    if (oldM === newM) return 1;
    return (1 + newM / 100) / (1 + oldM / 100);
  }, [effectiveEstimateMarginRate, isAgencyView, marginAppliedAtLoad]);

  const parsedCircuit = useMemo(
    () => parsedEstimate.circuit,
    [parsedEstimate.circuit],
  );

  const estimateDisplayTitle = useMemo(
    () =>
      resolveEstimateDisplayTitle({
        estimateName: estimateResponse.name,
        circuitName: parsedCircuit.name,
        showQuoteName: estimateResponse.showQuoteName,
        allowAgencyModification: estimateResponse.allowAgencyModification,
        viewContext: estimateResponse.viewContext,
      }),
    [
      estimateResponse.allowAgencyModification,
      estimateResponse.name,
      estimateResponse.showQuoteName,
      estimateResponse.viewContext,
      parsedCircuit.name,
    ],
  );

  const hideSummaryAndImportantPoints = useMemo(
    () =>
      shouldHideAgencyFlexibilityContent(
        estimateResponse,
        'showSummaryAndImportantPoints',
      ),
    [estimateResponse],
  );
  const hidePriceIncludesExcludes = useMemo(
    () =>
      shouldHideAgencyFlexibilityContent(
        estimateResponse,
        'showPriceIncludesExcludes',
      ),
    [estimateResponse],
  );
  const hideModificationCancellationConditions = useMemo(
    () =>
      shouldHideAgencyFlexibilityContent(
        estimateResponse,
        'showModificationCancellationConditions',
      ),
    [estimateResponse],
  );

  const muteQuoteName = useMemo(
    () => shouldMuteAgencyFlexibilityContent(estimateResponse, 'showQuoteName'),
    [estimateResponse],
  );
  const muteSummaryAndImportantPoints = useMemo(
    () =>
      shouldMuteAgencyFlexibilityContent(
        estimateResponse,
        'showSummaryAndImportantPoints',
      ),
    [estimateResponse],
  );
  const mutePriceIncludesExcludes = useMemo(
    () =>
      shouldMuteAgencyFlexibilityContent(
        estimateResponse,
        'showPriceIncludesExcludes',
      ),
    [estimateResponse],
  );
  const muteModificationCancellationConditions = useMemo(
    () =>
      shouldMuteAgencyFlexibilityContent(
        estimateResponse,
        'showModificationCancellationConditions',
      ),
    [estimateResponse],
  );

  useEffect(() => {
    if (currStep === 0) {
      reset(parsedEstimate);
    }
  }, [currStep, parsedEstimate, reset]);

  const [selectedArrivalDate, setSelectedArrivalDate] = useState<Date | null>(
    estimateResponse.arrivalDate
      ? new Date(estimateResponse.arrivalDate)
      : // Calendrier masqué (souplesse TO) : on fige une date par défaut pour
        // permettre à l'agence de poursuivre sans sélection.
        hideAgencyDepartureCalendar
        ? startOfToday()
        : null,
  );

  const targetDates = useMemo(() => {
    if (!selectedArrivalDate || !parsedCircuit.nbDays) return undefined;
    return Array.from({ length: parsedCircuit.nbDays }, (_, i) =>
      addDays(selectedArrivalDate, i),
    );
  }, [selectedArrivalDate, parsedCircuit.nbDays]);

  const {
    findMinimumPriceForDate,
    daysTrackingMultiDayBenefits,
    lastPricedStartDate,
    ensurePricedUntil,
  } = useCircuitPrices({
    days: parsedCircuit.circuitDays,
    circuitMarginRate: parsedCircuit.marginRate,
    nbParticipants: nbAdults + nbChildren,
    nbChildren,
    childrenAges,
    targetDates,
  });

  const parsedCircuitWithMultiDayBenefitTracking = useMemo(
    () => ({ ...parsedCircuit, circuitDays: daysTrackingMultiDayBenefits }),
    [parsedCircuit, daysTrackingMultiDayBenefits],
  );

  const defaultInternationalFlightPrice = useMemo(() => {
    if (isToView && estimateResponse.allowAgencyInternationalFlights === true) {
      return 0;
    }
    if (isClientLink) {
      return Number(estimateResponse.internationalFlightsPrice) || 0;
    }
    if (isAgencyView) {
      const agencyEntersFlights =
        estimateResponse.allowAgencyInternationalFlights === true;
      if (agencyEntersFlights) {
        return computeInternationalFlightsTotal({
          nonMarginedTaxes:
            estimateResponse.agencyInternationalFlightsNonMarginedTaxes ??
            estimateResponse.internationalFlightsNonMarginedTaxes,
          htAmount:
            estimateResponse.agencyInternationalFlightsPrice ??
            estimateResponse.internationalFlightsPrice,
          isMargined:
            estimateResponse.agencyMarginRateExistsForInternationalFlights ??
            estimateResponse.marginRateExistsForInternationalFlights,
          flightsMarginRate:
            estimateResponse.agencyInternationalFlightsMarginRate ??
            estimateResponse.internationalFlightsMarginRate,
        });
      }
      return computeInternationalFlightsTotal({
        nonMarginedTaxes: estimateResponse.internationalFlightsNonMarginedTaxes,
        htAmount: estimateResponse.internationalFlightsPrice,
        isMargined: estimateResponse.marginRateExistsForInternationalFlights,
        flightsMarginRate: estimateResponse.internationalFlightsMarginRate,
      });
    }
    return computeInternationalFlightsTotal({
      nonMarginedTaxes: estimateResponse.internationalFlightsNonMarginedTaxes,
      htAmount: estimateResponse.internationalFlightsPrice,
      isMargined: estimateResponse.marginRateExistsForInternationalFlights,
      flightsMarginRate: estimateResponse.internationalFlightsMarginRate,
    });
  }, [
    estimateResponse.agencyInternationalFlightsNonMarginedTaxes,
    estimateResponse.agencyInternationalFlightsMarginRate,
    estimateResponse.agencyInternationalFlightsPrice,
    estimateResponse.agencyMarginRateExistsForInternationalFlights,
    estimateResponse.allowAgencyInternationalFlights,
    estimateResponse.internationalFlightsNonMarginedTaxes,
    estimateResponse.internationalFlightsMarginRate,
    estimateResponse.internationalFlightsPrice,
    estimateResponse.marginRateExistsForInternationalFlights,
    isAgencyView,
    isClientLink,
    isToView,
  ]);

  const findMinimumCircuitPriceForDate = useCallback(
    (date: Date) =>
      roundUpPrice(findMinimumPriceForDate(date) * agencyMarginPriceFactor),
    [agencyMarginPriceFactor, findMinimumPriceForDate],
  );

  const pricesByStartDate = useMemo(
    () =>
      new Map(
        getDatesToPrice(targetDates).map((date) => [
          date.toISOString(),
          findMinimumCircuitPriceForDate(date),
        ]),
      ),
    [findMinimumCircuitPriceForDate, targetDates],
  );

  const totalPriceForAll = useMemo(
    () =>
      roundUpPrice(
        Array.from(pricesByStartDate.values())
          .filter((price) => price > 0)
          .reduce((a, b) => Math.min(a, b), Infinity),
      ),
    [pricesByStartDate],
  );

  const resolvedTotalPriceForAll = useMemo(() => {
    if (
      (isAgencyView || isClientLink) &&
      estimateResponse.agencyAmount != null &&
      estimateResponse.status !== EstimateStatus.PENDING
    ) {
      return roundUpPrice(estimateResponse.agencyAmount);
    }
    return totalPriceForAll;
  }, [
    estimateResponse.agencyAmount,
    estimateResponse.status,
    isAgencyView,
    isClientLink,
    totalPriceForAll,
  ]);

  /** Final total shown in the side card, incl. agency extra margin when needed. */
  const displayedTotalForAll = useMemo(() => {
    if (
      (isAgencyView || isClientLink) &&
      estimateResponse.agencyAmount != null &&
      estimateResponse.status !== EstimateStatus.PENDING
    ) {
      return roundUpPrice(estimateResponse.agencyAmount);
    }
    return roundUpPrice(applyAgencyAdditionalMargin(resolvedTotalPriceForAll));
  }, [
    applyAgencyAdditionalMargin,
    estimateResponse.agencyAmount,
    estimateResponse.status,
    isAgencyView,
    isClientLink,
    resolvedTotalPriceForAll,
  ]);

  /**
   * Side card total for the selected departure date (step 2+). The calendar
   * header uses the same date-specific price; do not reuse the 11-month minimum
   * (`displayedTotalForAll`) which can be lower on another departure day.
   */
  const displayedTotalForSelectedDate = useMemo(() => {
    if (
      (isAgencyView || isClientLink) &&
      estimateResponse.agencyAmount != null &&
      estimateResponse.status !== EstimateStatus.PENDING
    ) {
      return displayedTotalForAll;
    }
    if (!selectedArrivalDate) return displayedTotalForAll;
    const dayTotal = pricesByStartDate.get(selectedArrivalDate.toISOString());
    if (dayTotal == null || dayTotal <= 0) return displayedTotalForAll;
    return roundUpPrice(applyAgencyAdditionalMargin(dayTotal));
  }, [
    pricesByStartDate,
    applyAgencyAdditionalMargin,
    displayedTotalForAll,
    estimateResponse.agencyAmount,
    estimateResponse.status,
    isAgencyView,
    isClientLink,
    selectedArrivalDate,
  ]);

  const circuitWithGeneralMarginForSummary = useMemo(() => {
    if (!isAgencyView) return undefined;

    if (
      estimateResponse.agencyAmount != null &&
      estimateResponse.status !== EstimateStatus.PENDING &&
      estimateResponse.amount != null
    ) {
      const rate = effectiveEstimateMarginRate;
      return rate > 0
        ? roundUpPrice(estimateResponse.amount * (1 + rate / 100))
        : estimateResponse.amount;
    }

    if (!selectedArrivalDate) return undefined;
    return pricesByStartDate.get(selectedArrivalDate.toISOString()) ?? 0;
  }, [
    pricesByStartDate,
    effectiveEstimateMarginRate,
    estimateResponse.agencyAmount,
    estimateResponse.amount,
    estimateResponse.status,
    isAgencyView,
    selectedArrivalDate,
  ]);

  const totalPrice = useMemo(
    () => roundUpPrice(displayedTotalForAll / (nbAdults + nbChildren)),
    [nbAdults, nbChildren, displayedTotalForAll],
  );

  const resolvedInternationalFlightPrice = useMemo(() => {
    if (
      (isAgencyView || isClientLink) &&
      estimateResponse.agencyAmount != null &&
      estimateResponse.status !== EstimateStatus.PENDING
    ) {
      return 0;
    }
    return defaultInternationalFlightPrice;
  }, [
    defaultInternationalFlightPrice,
    estimateResponse.agencyAmount,
    estimateResponse.status,
    isAgencyView,
    isClientLink,
  ]);

  const getDayPrice = useCallback(
    (date: Date) => {
      // Les dates au-delà de la fenêtre initiale sont calculées à la volée,
      // sur les prix étendus quand le calendrier avance (ensurePricedUntil).
      const storedDayTotal = pricesByStartDate.get(date.toISOString());
      const dayCircuitTotal =
        storedDayTotal ?? findMinimumCircuitPriceForDate(date);
      if (storedDayTotal == null && !Number.isFinite(dayCircuitTotal))
        return roundUpPrice(Infinity);
      const usesStoredAgencyTotal =
        (isAgencyView || isClientLink) &&
        estimateResponse.agencyAmount != null &&
        estimateResponse.status !== EstimateStatus.PENDING;
      const dayTotalWithFlights =
        dayCircuitTotal + resolvedInternationalFlightPrice;
      const perPerson = usesStoredAgencyTotal
        ? displayedTotalForAll
        : applyAgencyAdditionalMargin(dayTotalWithFlights);
      return roundUpPrice(perPerson / (nbAdults + nbChildren));
    },
    [
      pricesByStartDate,
      applyAgencyAdditionalMargin,
      displayedTotalForAll,
      estimateResponse.agencyAmount,
      estimateResponse.status,
      findMinimumCircuitPriceForDate,
      isAgencyView,
      isClientLink,
      nbAdults,
      nbChildren,
      resolvedInternationalFlightPrice,
    ],
  );

  const displayedEstimatePrice = useMemo(() => {
    if (!selectedArrivalDate) return totalPrice;
    return getDayPrice(selectedArrivalDate);
  }, [getDayPrice, selectedArrivalDate, totalPrice]);

  const daysItinerary = useMemo(
    () =>
      parsedCircuit.circuitDays.map(({ stepTitle, description, images }) => ({
        stepTitle,
        description,
        images: images as string[],
      })),
    [parsedCircuit.circuitDays],
  );

  const images = useMemo(
    () =>
      estimateResponse.EstimateCircuit.circuit.mainImage
        ? [estimateResponse.EstimateCircuit.circuit.mainImage].concat(
            estimateResponse.EstimateCircuit.circuit.images,
          )
        : estimateResponse.EstimateCircuit.circuit.images,
    [
      estimateResponse.EstimateCircuit.circuit.mainImage,
      estimateResponse.EstimateCircuit.circuit.images,
    ],
  );

  const isDateSelectable = useCallback(
    (date: Date) => {
      const canPickDate =
        estimateResponse.status === EstimateStatus.PENDING ||
        (isAgencyView && estimateResponse.status === EstimateStatus.ESTIMATE);
      if (!canPickDate) return false;
      if (!canSelectDepartureDate) return false;
      const price = getDayPrice(date);
      return price !== Infinity && price >= 0;
    },
    [
      canSelectDepartureDate,
      estimateResponse.status,
      getDayPrice,
      isAgencyView,
    ],
  );

  const mainContact = useMemo(
    () => estimateResponse.project.client?.mainContact,
    [estimateResponse.project.client?.mainContact],
  );

  const breadcrumbTitle = useMemo(() => {
    if (source !== 'projectEstimates') return t('common.backToList');
    const client = estimateResponse.project.client;
    const project = estimateResponse.project;
    if (isTourOperator && client) {
      const agencyLabel =
        client.agencyName ??
        `${mainContact?.firstName ?? ''} ${mainContact?.lastName ?? ''}`.trim();
      const clientLabel = project.clientFirstName
        ? `${project.name} ${project.clientFirstName}`
        : project.name;
      return `${agencyLabel} / ${clientLabel}`;
    }
    return `${mainContact?.firstName ?? ''} ${mainContact?.lastName ?? ''} / ${t('clients.projects')} / ${project.name}`;
  }, [source, estimateResponse.project, isTourOperator, mainContact, t]);

  const computeOptionalBenefitPrice = useCallback(
    (
      benefit: BenefitFormData,
      date: Date,
      currIndex: number,
      nbDays = 1,
      nbAdultsOverride?: number,
      nbAdolescentsOverride?: number,
      nbChildrenOverride?: number,
      selectedHotelPensionType?: string,
    ) => {
      const { marginRate, prices } = computeBenefitPrices({
        benefit,
        selectedHotelPensionType,
        indexDay: currIndex,
        nbParticipants: nbAdultsOverride
          ? nbAdultsOverride +
            (nbChildrenOverride ?? 0) +
            (nbAdolescentsOverride ?? 0)
          : nbAdults + nbChildren,
        circuitMarginRate: parsedCircuit.marginRate,
        nbChildren: nbChildrenOverride ?? nbChildren,
        nbAdolescents: nbAdolescentsOverride ?? undefined,
        nbDays,
        childrenAges:
          nbChildrenOverride != undefined ? undefined : childrenAges,
        targetDates,
      });
      const rawPrice = prices.get(priceDateKey(date))?.minimumPrice;
      return roundUpPrice(
        (rawPrice ?? Infinity) *
          (1 + marginRate / 100) *
          agencyMarginPriceFactor,
      );
    },
    [
      agencyMarginPriceFactor,
      childrenAges,
      nbAdults,
      nbChildren,
      parsedCircuit.marginRate,
      targetDates,
    ],
  );

  const { submitForm } = useSubmitEstimate({
    projectId,
    estimateId: estimateResponse.id,
    accessToken,
    pricesByStartDate,
    setIsReservationRequestAcceptedModalOpen,
    setIsReservationBlockedModalOpen,
    adults: estimateResponse.adults,
    children: estimateResponse.children,
    isAgencyView,
    isToView,
    isClientLink,
  });

  const requestSaveEstimate = useCallback(
    (options?: Record<string, SelectedEstimateOption>) => {
      if (!selectedArrivalDate) return;
      handleSubmit(
        submitForm(selectedArrivalDate, false, options),
        (errors) => {
          const detail = formatFirstFormError<EstimateFormData>(errors);
          toastAsync(Promise.reject(new Error('validation')), {
            error: detail ?? t('estimates.saveValidationFailed'),
          });
        },
      )();
    },
    [handleSubmit, selectedArrivalDate, submitForm, t, toastAsync],
  );

  const requestReservation = useCallback(
    (options?: Record<string, SelectedEstimateOption>) => {
      if (!selectedArrivalDate) return;
      handleSubmit(submitForm(selectedArrivalDate, true, options), (errors) => {
        const detail = formatFirstFormError<EstimateFormData>(errors);
        toastAsync(Promise.reject(new Error('validation')), {
          error: detail ?? t('estimates.saveValidationFailed'),
        });
      })();
    },
    [handleSubmit, selectedArrivalDate, submitForm, t, toastAsync],
  );

  const { mutateAsync: getEstimateSharedLinkToken } =
    useGetEstimateSharedLinkToken(estimateResponse.id);

  const [showFallbackTextarea, setShowFallbackTextarea] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState('');

  const copyEstimateLink = useCallback(async () => {
    try {
      const { token } = await getEstimateSharedLinkToken();
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const url =
        baseUrl +
        PATHS.protected.estimate
          .replace(':estimateId', estimateResponse.id)
          .replace(':projectId', projectId)
          .concat(`?accessToken=${token}`);

      try {
        await navigator.clipboard.writeText(url);
        setShowFallbackTextarea(false);
        toastAsync(Promise.resolve(), {
          success: t('estimates.details.sideCard.linkCopied'),
        });
      } catch {
        setFallbackUrl(url);
        setShowFallbackTextarea(true);
        toastAsync(Promise.resolve(), {
          success: t('estimates.details.sideCard.linkCopyFailed'),
        });
      }
    } catch (error) {
      console.error('Failed to get shared link token:', error);
      toastAsync(Promise.reject(error), {
        error: t('estimates.details.sideCard.linkGenerationFailed'),
      });
    }
  }, [
    estimateResponse.id,
    getEstimateSharedLinkToken,
    projectId,
    t,
    toastAsync,
  ]);

  const { mutateAsync: revokeSharedLink } = useRevokeSharedLink(
    estimateResponse.id,
  );

  const distributorLinkType: SharedLinkType =
    isToView && estimateResponse.allowAgencyModification ? 'AGENCY' : 'CLIENT';

  const distributorLinkActive = isSharedLinkActive(
    estimateResponse.SharedLink,
    distributorLinkType,
  );

  const clientLinkActive = isSharedLinkActive(
    estimateResponse.SharedLink,
    'CLIENT',
  );

  const [isEstimateSharable, setIsEstimateSharable] = useState(
    distributorLinkActive,
  );

  useEffect(() => {
    setIsEstimateSharable(distributorLinkActive);
  }, [distributorLinkActive]);

  const onChangeIsEstimateSharable = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        copyEstimateLink();
      } else {
        toastAsync(revokeSharedLink(), {
          success: t('estimates.details.sideCard.linkRevoked'),
        });
      }
      setIsEstimateSharable(isChecked);
    },
    [copyEstimateLink, revokeSharedLink, t, toastAsync],
  );

  // Agency view only: mint/revoke the downstream CLIENT link using the current
  // AGENCY accessToken. The generated URL is the standard estimate link with
  // ?accessToken=<clientToken>.
  const { mutateAsync: createClientSharedLink } = useCreateClientSharedLink(
    projectId,
    estimateResponse.id,
    accessToken,
  );
  const { mutateAsync: revokeClientSharedLink } = useRevokeClientSharedLink(
    projectId,
    estimateResponse.id,
    accessToken,
  );

  const [showClientFallbackTextarea, setShowClientFallbackTextarea] =
    useState(false);
  const [clientFallbackUrl, setClientFallbackUrl] = useState('');
  const [isClientLinkSharable, setIsClientLinkSharable] =
    useState(clientLinkActive);

  useEffect(() => {
    setIsClientLinkSharable(clientLinkActive);
  }, [clientLinkActive]);

  const copyClientLink = useCallback(async () => {
    try {
      const { token } = await createClientSharedLink();
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const url =
        baseUrl +
        PATHS.protected.estimate
          .replace(':estimateId', estimateResponse.id)
          .replace(':projectId', projectId)
          .concat(`?accessToken=${token}`);

      try {
        await navigator.clipboard.writeText(url);
        setShowClientFallbackTextarea(false);
        toastAsync(Promise.resolve(), {
          success: t('estimates.details.sideCard.linkCopied'),
        });
      } catch {
        setClientFallbackUrl(url);
        setShowClientFallbackTextarea(true);
        toastAsync(Promise.resolve(), {
          success: t('estimates.details.sideCard.linkCopyFailed'),
        });
      }
    } catch (error) {
      console.error('Failed to create client shared link:', error);
      toastAsync(Promise.reject(error), {
        error: t('estimates.details.sideCard.linkGenerationFailed'),
      });
    }
  }, [createClientSharedLink, estimateResponse.id, projectId, t, toastAsync]);

  const onChangeIsClientLinkSharable = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        copyClientLink();
      } else {
        toastAsync(revokeClientSharedLink(), {
          success: t('estimates.details.sideCard.linkRevoked'),
        });
      }
      setIsClientLinkSharable(isChecked);
    },
    [copyClientLink, revokeClientSharedLink, t, toastAsync],
  );

  const defaultSelectedOptions = useWatch({
    control,
    name: 'selectedEstimateOptions',
  });

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, SelectedEstimateOption>
  >(defaultSelectedOptions as Record<string, SelectedEstimateOption>);

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={estimateDisplayTitle}
          titleMuted={muteQuoteName}
          code={
            accessToken ? undefined : formatEstimateRef(estimateResponse.ref)
          }
          breadcrumb={
            accessToken ? null : (
              <StyledButton
                variant="link"
                title={breadcrumbTitle}
                className="text-sm text-[#3D3D3D] pl-0"
                onClick={() => navigate(estimatesListPath)}
                icon={<ArrowLeftIcon />}
              />
            )
          }
          subHeading={estimateResponse.EstimateCircuit.circuit.countries
            .map((country) => getTranslatedCountry(country, estimateLanguage))
            .join(', ')}
          subTitle={t('circuits.duration', {
            days: parsedCircuit.nbDays,
            nights: parsedCircuit.nbNights,
          })}
        >
          {!shouldHideInternationalFlights && (
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm leading-6 font-medium text-black">
                  {t('estimates.details.sideCard.accessibleLink')}
                </span>
                <Switch
                  checked={isEstimateSharable}
                  onCheckedChange={onChangeIsEstimateSharable}
                />
              </div>
              <StyledButton
                title={t('estimates.details.sideCard.copyLink')}
                variant="outlined"
                className="px-3 py-2 rounded-full"
                icon={<LinkIcon className="size-5" />}
                onClick={copyEstimateLink}
                disabled={!isEstimateSharable}
              />
              {showFallbackTextarea && (
                <textarea
                  readOnly
                  value={fallbackUrl}
                  className="mt-2 p-2 border rounded w-full"
                  rows={2}
                />
              )}
            </div>
          )}
          {isAgencyView && (
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm leading-6 font-medium text-black">
                  {t('estimates.agencyView.clientLink.accessibleLink')}
                </span>
                <Switch
                  checked={isClientLinkSharable}
                  onCheckedChange={onChangeIsClientLinkSharable}
                />
              </div>
              <StyledButton
                title={t('estimates.agencyView.clientLink.copyLink')}
                variant="outlined"
                className="px-3 py-2 rounded-full"
                icon={<LinkIcon className="size-5" />}
                onClick={copyClientLink}
                disabled={!isClientLinkSharable}
              />
              {showClientFallbackTextarea && (
                <textarea
                  readOnly
                  value={clientFallbackUrl}
                  className="mt-2 p-2 border rounded w-full"
                  rows={2}
                />
              )}
            </div>
          )}
        </StyledFormHeaderWithBreadcrumb>
        <HorizontalLine />
        <div className="mt-8">
          <StyledStepper
            steps={ESTIMATE_STEPS}
            activeStep={currStep}
            disableNext={currStep === 0 && selectedArrivalDate === null}
            setStep={setCurrStep}
          />
        </div>
        <HorizontalLine />
        {currStep === 0 ? (
          <div className="flex flex-col lg:flex-row-reverse gap-4 p-6">
            <div className="w-fit h-fit lg:sticky lg:top-20">
              <CircuitDetailsParticipantsCard
                minimumPrice={displayedEstimatePrice}
                nbAdults={nbAdults}
                setNbAdults={setNbAdults}
                nbChildren={nbChildren}
                setNbChildren={setNbChildren}
                childrenBirthDates={childrenBirthDates}
                setChildrenBirthDates={setChildrenBirthDates}
                defaultNbParticipants={nbAdults + nbChildren}
                selectedArrivalDate={selectedArrivalDate}
                onSelectOptionsButtonClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                shouldShowParticipantsSetterButtons={false}
                displayDateSelector={canSelectDepartureDate}
                mainBtnLabel={t('circuits.participants.chooseOptions')}
                isViewMode
                secondaryBtnLabel={
                  canEditCircuitParagraphs ? t('common.save') : undefined
                }
                onSecondaryBtnClick={() => requestSaveEstimate(selectedOptions)}
                secondaryBtnDisabled={!selectedArrivalDate}
              />
            </div>
            <div className="flex flex-col gap-4 w-full">
              <CircuitDetailsImagesCard
                circuitId={estimateResponse.EstimateCircuit.circuit.id}
                images={images}
                accessToken={accessToken}
              />
              {!hideSummaryAndImportantPoints && (
                <>
                  <CircuitDetailsSummaryCard
                    summary={parsedCircuit.summary ?? ''}
                    title={t('circuits.editorial.details.summary')}
                    muted={muteSummaryAndImportantPoints}
                    edit={{
                      control,
                      name: 'summary',
                      isViewMode: !canEditCircuitParagraphs,
                      onValidateParagraph: canEditCircuitParagraphs
                        ? (v) => handleValidateParagraph('summary', v)
                        : undefined,
                    }}
                  />
                  <CircuitDetailsSummaryCard
                    summary={parsedCircuit.importantPoints ?? ''}
                    title={t('circuits.editorial.details.importantPoints')}
                    muted={muteSummaryAndImportantPoints}
                    edit={{
                      control,
                      name: 'importantPoints',
                      isViewMode: !canEditCircuitParagraphs,
                      onValidateParagraph: canEditCircuitParagraphs
                        ? (v) => handleValidateParagraph('importantPoints', v)
                        : undefined,
                    }}
                  />
                </>
              )}
              <div id="calendar">
                {canSelectDepartureDate && !hideAgencyDepartureCalendar ? (
                  <CircuitDetailsPricesCalendarCard
                    calculateMinimumPriceForDate={getDayPrice}
                    nbParticipants={nbAdults + nbChildren}
                    onDayClick={(date) => {
                      if (isDateSelectable(date)) setSelectedArrivalDate(date);
                    }}
                    defaultDate={selectedArrivalDate || startOfToday()}
                    foldable={false}
                    lastPricedStartDate={lastPricedStartDate}
                    onMonthChange={ensurePricedUntil}
                  />
                ) : null}
              </div>
              <CircuitDetailsItineraryCard
                itinerary={daysItinerary}
                control={canEditCircuitParagraphs ? control : undefined}
                onValidateParagraph={
                  canEditCircuitParagraphs ? handleValidateParagraph : undefined
                }
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {!hidePriceIncludesExcludes && (
                  <>
                    <CircuitDetailsSummaryCard
                      title={t('circuits.editorial.details.priceIncludes')}
                      summary={parsedCircuit.priceIncludes ?? ''}
                      textBrand
                      muted={mutePriceIncludesExcludes}
                      edit={{
                        control,
                        name: 'priceIncludes',
                        isViewMode: !canEditCircuitParagraphs,
                        onValidateParagraph: canEditCircuitParagraphs
                          ? (v) => handleValidateParagraph('priceIncludes', v)
                          : undefined,
                      }}
                    />
                    <CircuitDetailsSummaryCard
                      title={t('circuits.editorial.details.priceExcludes')}
                      summary={parsedCircuit.priceExcludes ?? ''}
                      textBrand
                      muted={mutePriceIncludesExcludes}
                      edit={{
                        control,
                        name: 'priceExcludes',
                        isViewMode: !canEditCircuitParagraphs,
                        onValidateParagraph: canEditCircuitParagraphs
                          ? (v) => handleValidateParagraph('priceExcludes', v)
                          : undefined,
                      }}
                    />
                  </>
                )}
                {!hideModificationCancellationConditions && (
                  <>
                    <CircuitDetailsSummaryCard
                      title={t(
                        'circuits.editorial.details.modificationConditions',
                      )}
                      summary={parsedCircuit.modificationsConditions ?? ''}
                      textBrand
                      muted={muteModificationCancellationConditions}
                      edit={{
                        control,
                        name: 'modificationsConditions',
                        isViewMode: !canEditCircuitParagraphs,
                        onValidateParagraph: canEditCircuitParagraphs
                          ? (v) =>
                              handleValidateParagraph(
                                'modificationsConditions',
                                v,
                              )
                          : undefined,
                      }}
                    />
                    <CircuitDetailsSummaryCard
                      title={t(
                        'circuits.editorial.details.cancellationConditions',
                      )}
                      summary={parsedCircuit.cancellationConditions ?? ''}
                      textBrand
                      muted={muteModificationCancellationConditions}
                      edit={{
                        control,
                        name: 'cancellationConditions',
                        isViewMode: !canEditCircuitParagraphs,
                        onValidateParagraph: canEditCircuitParagraphs
                          ? (v) =>
                              handleValidateParagraph(
                                'cancellationConditions',
                                v,
                              )
                          : undefined,
                      }}
                    />
                  </>
                )}
              </div>
              <div className="w-fit h-fit lg:hidden">
                <CircuitDetailsParticipantsCard
                  minimumPrice={displayedEstimatePrice}
                  nbAdults={nbAdults}
                  setNbAdults={setNbAdults}
                  nbChildren={nbChildren}
                  setNbChildren={setNbChildren}
                  childrenBirthDates={childrenBirthDates}
                  setChildrenBirthDates={setChildrenBirthDates}
                  defaultNbParticipants={nbAdults + nbChildren}
                  selectedArrivalDate={selectedArrivalDate}
                  onSelectOptionsButtonClick={() => {
                    setCurrStep(1);
                    window.scrollTo(0, 0);
                  }}
                  shouldShowParticipantsSetterButtons={false}
                  displayDateSelector={canSelectDepartureDate}
                  mainBtnLabel={t('circuits.participants.chooseOptions')}
                  isViewMode
                  secondaryBtnLabel={
                    canEditCircuitParagraphs ? t('common.save') : undefined
                  }
                  onSecondaryBtnClick={() =>
                    requestSaveEstimate(selectedOptions)
                  }
                  secondaryBtnDisabled={!selectedArrivalDate}
                />
              </div>
            </div>
          </div>
        ) : (
          selectedArrivalDate && (
            <EstimateDetailsAndOptions
              estimateTitle={estimateDisplayTitle}
              language={estimateLanguage}
              circuit={
                parsedCircuitWithMultiDayBenefitTracking as CircuitFormData
              }
              arrivalDate={selectedArrivalDate}
              adults={nbAdults}
              children={nbChildren}
              totalPrice={displayedTotalForSelectedDate}
              control={control}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              estimateMarginRate={effectiveEstimateMarginRate}
              distributorMarginPriceFactor={agencyMarginPriceFactor}
              computeOptionalBenefitPrice={computeOptionalBenefitPrice}
              goToLastStep={() => {
                setCurrStep(2);
                window.scrollTo(0, 0);
              }}
              saveEstimate={requestSaveEstimate}
              sendReservationRequest={requestReservation}
              status={estimateResponse.status}
              isViewMode={currStep === 2}
              shouldHideInternationalFlights={
                shouldHideInternationalFlights ||
                (isToView &&
                  estimateResponse.allowAgencyInternationalFlights === true)
              }
              accessToken={accessToken}
              isAgencyView={isAgencyView}
              isToView={isToView}
              isClientLink={isClientLink}
              hideReservationConfirm={hideClientReservationConfirm}
              clientHasSentRequest={clientHasSentRequest}
              clientRequestAlreadySent={clientRequestAlreadySent}
              allowAgencyMarginRate={
                estimateResponse.allowAgencyMarginRate === true
              }
              allowAgencyInternationalFlights={
                estimateResponse.allowAgencyInternationalFlights === true
              }
              allowAgencyAdditionalMargin={
                estimateResponse.allowAgencyAdditionalMargin === true
              }
              circuitWithGeneralMarginForSummary={
                circuitWithGeneralMarginForSummary
              }
              frozenAdditionalMarginType={
                estimateResponse.agencyAdditionalMarginType ?? undefined
              }
              frozenAdditionalMarginValue={
                estimateResponse.agencyAdditionalMarginValue
              }
              hidePriceIncludesExcludes={hidePriceIncludesExcludes}
              hideModificationCancellationConditions={
                hideModificationCancellationConditions
              }
              mutePriceIncludesExcludes={mutePriceIncludesExcludes}
              muteModificationCancellationConditions={
                muteModificationCancellationConditions
              }
              titleMuted={muteQuoteName}
              agencyAmount={estimateResponse.agencyAmount}
            />
          )
        )}
        {isReservationRequestAcceptedModalOpen && (
          <ReservationRequestAcceptedModal
            closeModal={() => {
              setIsReservationRequestAcceptedModalOpen(false);
              // Shared-link viewers (client / agency) stay on the estimate page.
              if (!accessToken) {
                navigate(estimatesListPath);
              }
            }}
          />
        )}
        {isReservationBlockedModalOpen && (
          <Modal
            closeModal={() => setIsReservationBlockedModalOpen(false)}
            title={t('estimates.reservationBlocked.title')}
            message={t('estimates.reservationBlocked.message')}
            actionTitle={t('common.close')}
            actionOnClick={() => setIsReservationBlockedModalOpen(false)}
            hasAction={false}
            cancelBtnLabel={t('common.close')}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};
